/**
 * Iter 67 — Labour face enrollment (3-pose, kiosk-style).
 *
 * Site engineer picks a labour record (or imports new) → captures 3 poses
 * (front / left / right) → POST enroll-face with all descriptors in one shot.
 * Mirrors the employee FaceEnrollment.jsx flow for consistent matching
 * accuracy across employees + labour.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Check, Loader2, ArrowRight } from 'lucide-react';
import FaceCaptureView from '../../components/FaceCaptureView';
import { useDeviceGuard, DesktopBlockScreen } from '../../components/DeviceGuard';
import { apiGet, apiPost } from '../../lib/api';
import { loadFaceModels } from '../../lib/faceRecognition';

const POSES = [
  { key: 'front', label: 'Front', hint: 'Worker faces the camera straight on.', tip: 'Hold the phone at eye level.' },
  { key: 'left',  label: 'Left',  hint: 'Ask the worker to turn head LEFT ~30°.', tip: 'Keep both eyes visible.' },
  { key: 'right', label: 'Right', hint: 'Ask the worker to turn head RIGHT ~30°.', tip: 'Keep both eyes visible.' },
];

export default function LabourFaceEnroll() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const deviceAllowed = useDeviceGuard('enroll');
  const preLabourId = params.get('labour_id');
  const fromContext = params.get('from'); // 'register' | 'list'
  const [labour, setLabour] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState('');
  const [step, setStep] = useState('pick'); // 'pick' | 'capture' | 'review' | 'submitting' | 'done'
  const [poseIdx, setPoseIdx] = useState(0);
  const [captures, setCaptures] = useState([]); // [{pose, descriptor, photoDataUrl}]
  const [submitting, setSubmitting] = useState(false);
  const [doneFor, setDoneFor] = useState(null);

  useEffect(() => {
    loadFaceModels().catch(() => {});   // pre-warm models before first capture
    apiGet('/labour-master').then((r) => {
      const items = Array.isArray(r) ? r : (r?.items || []);
      setLabour(items);
      if (preLabourId && !selected) {
        const found = items.find((l) => l.id === preLabourId);
        if (found) { setSelected(found); setStep('capture'); }
        else if (params.get('name')) {
          setSelected({ id: preLabourId, name: params.get('name'), aadhar_number: '••••••••' });
          setStep('capture');
        }
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preLabourId]);

  const filtered = labour.filter((l) => {
    if (l.face_enrolled) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (l.name || '').toLowerCase().includes(s) || (l.aadhar_number || '').includes(s);
  });

  const pickLabour = (l) => {
    setSelected(l);
    setCaptures([]);
    setPoseIdx(0);
    setStep('capture');
  };

  const resetAll = () => {
    setSelected(null);
    setCaptures([]);
    setPoseIdx(0);
    setStep('pick');
  };

  const onCapture = ({ descriptor, photoDataUrl }) => {
    const pose = POSES[poseIdx];
    setCaptures((prev) => {
      const next = [...prev.filter((c) => c.pose !== pose.key), { pose: pose.key, descriptor, photoDataUrl }];
      return next;
    });
    toast.success(`${pose.label} pose captured`);
    if (poseIdx < POSES.length - 1) {
      setPoseIdx(poseIdx + 1);
    } else {
      setStep('review');
    }
  };

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setStep('submitting');
    try {
      const front = captures.find((c) => c.pose === 'front') || captures[0];
      await apiPost('/mobile/attendance/enroll-face', {
        entity_type: 'labour',
        entity_id: selected.id,
        descriptor: front.descriptor,
        descriptors: captures.map((c) => c.descriptor),
        photo_url: front.photoDataUrl,
        photos: captures.map((c) => c.photoDataUrl),
      });
      setDoneFor({ name: selected.name });
      setStep('done');
      toast.success(`${selected.name} enrolled · 3 poses saved`);
      // Refresh list for the next labour
      try {
        const r = await apiGet('/labour-master');
        setLabour(Array.isArray(r) ? r : (r?.items || []));
      } catch {}
      if (fromContext === 'register' || fromContext === 'list') {
        setTimeout(() => navigate('/m/labour-register'), 1600);
      } else {
        setTimeout(() => { setDoneFor(null); resetAll(); }, 1600);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enrolment failed');
      setStep('review');
    } finally { setSubmitting(false); }
  };

  // Field-only gate: labour face enrolment must happen on a site phone/tablet.
  if (deviceAllowed === null) return null;
  if (deviceAllowed === false) return <DesktopBlockScreen />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f1a2e] via-[#1e2a4d] to-[#0f1a2e] text-white pb-24">
      <header className="px-5 pt-7 pb-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-display font-bold">Field Enrolment · 3 Poses</div>
          <div className="font-display font-bold text-2xl mt-0.5">Labour Face Capture</div>
        </div>
        <button onClick={() => navigate(-1)} className="text-xs text-white/60 hover:text-white">← Back</button>
      </header>

      {step === 'pick' && (
        <main className="px-5 space-y-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search labour by name or Aadhar…"
              className="w-full pl-9 pr-3 py-3 bg-white/10 border border-white/15 rounded-xl text-sm placeholder-white/40 focus:outline-none focus:border-white/40"
              data-testid="labour-enroll-search"
            />
          </div>
          <p className="text-[11px] text-white/50">{filtered.length} pending face enrolment</p>
          <ul className="space-y-2">
            {filtered.map((l) => (
              <li key={l.id}>
                <button
                  onClick={() => pickLabour(l)}
                  className="w-full text-left bg-white/10 border border-white/15 rounded-2xl p-3 hover:border-white/30"
                  data-testid={`labour-pick-${l.id}`}
                >
                  <div className="font-display font-bold text-sm">{l.name}</div>
                  <div className="text-[11px] text-white/60 mt-0.5 font-mono">{l.aadhar_number} · {l.trade || 'trade —'}</div>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="text-center text-sm text-white/50 py-10">All labour enrolled. Add new labour via the desktop Labour Master.</li>
            )}
          </ul>
        </main>
      )}

      {step === 'capture' && selected && (
        <main className="px-5">
          <div className="bg-white/10 border border-white/15 rounded-2xl px-4 py-3 mb-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Enrolling</div>
              <div className="font-display font-bold text-base">{selected.name}</div>
              <div className="text-[11px] text-white/60 font-mono">{selected.aadhar_number}</div>
            </div>
            <button onClick={resetAll} className="text-xs text-white/60 hover:text-white" data-testid="labour-enroll-change">Change</button>
          </div>

          <PoseStepper current={poseIdx} captured={captures.map((c) => c.pose)} />

          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 mb-3" data-testid="labour-pose-hint">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-display font-bold">
              Pose {poseIdx + 1} of 3 — {POSES[poseIdx].label}
            </div>
            <div className="text-sm font-display font-bold mt-1">{POSES[poseIdx].hint}</div>
            <div className="text-[11px] text-white/50 mt-1">{POSES[poseIdx].tip}</div>
          </div>

          <FaceCaptureView
            key={POSES[poseIdx].key /* remount so auto-capture re-arms */}
            onCapture={onCapture}
            busy={submitting}
            captureLabel={`Capture ${POSES[poseIdx].label}`}
            facingMode="environment"
            helpText={`${POSES[poseIdx].label} pose — hold steady while we verify…`}
            requiredPose={POSES[poseIdx].key}
          />

          {poseIdx > 0 && (
            <button
              onClick={() => setPoseIdx(poseIdx - 1)}
              className="mt-3 w-full text-center text-xs text-white/50 py-2"
              data-testid="labour-pose-back"
            >
              Back to {POSES[poseIdx - 1].label} pose
            </button>
          )}
        </main>
      )}

      {step === 'review' && selected && (
        <main className="px-5 pb-10 space-y-4">
          <div className="bg-white/10 border border-white/15 rounded-2xl px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Review for</div>
              <div className="font-display font-bold text-base">{selected.name}</div>
            </div>
            <button onClick={() => { setCaptures([]); setPoseIdx(0); setStep('capture'); }} className="text-xs text-white/60 hover:text-white" data-testid="labour-review-retake">
              Re-capture
            </button>
          </div>
          <div className="text-sm text-white/70 text-center">3 poses captured. Review and submit.</div>
          <div className="grid grid-cols-3 gap-2" data-testid="labour-review-grid">
            {POSES.map((p) => {
              const cap = captures.find((c) => c.pose === p.key);
              return (
                <div key={p.key} className="bg-white/5 border border-white/10 rounded-xl p-2 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-white/50 font-display font-bold mb-1">{p.label}</div>
                  {cap?.photoDataUrl ? (
                    <img src={cap.photoDataUrl} alt={p.label} className="w-full aspect-square object-cover rounded-md" />
                  ) : (
                    <div className="w-full aspect-square bg-white/5 rounded-md flex items-center justify-center text-white/30 text-xs">missing</div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            onClick={submit}
            disabled={submitting || captures.length < 3}
            data-testid="labour-review-submit"
            className="w-full bg-emerald-500 text-white font-display font-bold text-base py-4 rounded-full shadow-2xl active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <><Check size={16} /> Save All 3 Poses</>}
          </button>
        </main>
      )}

      {step === 'submitting' && (
        <main className="px-5 mt-20 text-center">
          <Loader2 size={32} className="animate-spin mx-auto" />
          <div className="mt-3 text-sm text-white/70">Saving 3 descriptors…</div>
        </main>
      )}

      {step === 'done' && doneFor && (
        <main className="px-5 mt-20">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center mb-4 shadow-2xl">
              <Check size={40} />
            </div>
            <h2 className="font-display font-bold text-2xl">{doneFor.name} enrolled</h2>
            <p className="text-sm text-white/60 mt-1">3 poses saved · matching accuracy boosted. Pick the next labour…</p>
            <button
              onClick={resetAll}
              className="mt-5 inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2 rounded-full font-display font-bold text-sm"
              data-testid="labour-enroll-next"
            >
              Next labour <ArrowRight size={14} />
            </button>
          </div>
        </main>
      )}
    </div>
  );
}

function PoseStepper({ current, captured }) {
  return (
    <div className="flex items-center gap-2 mb-3" data-testid="labour-pose-stepper">
      {POSES.map((p, i) => {
        const isDone = captured.includes(p.key);
        const isActive = i === current;
        return (
          <React.Fragment key={p.key}>
            <div className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-full text-[10px] font-display font-bold uppercase tracking-wider ${
              isDone ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/30'
              : isActive ? 'bg-white/15 text-white border border-white/30'
              : 'bg-white/5 text-white/40 border border-white/10'
            }`}>
              <span>{isDone ? <Check size={10} /> : i + 1}</span> {p.label}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
